import { SelectProjectRole } from "@components/role/select-project-role";
import { MemberRoleReadOnly } from "@components/table/members/members-column/role-column/member-role-read-only";
import { useCompanyId } from "@context-providers/company-id-provider";
import { grey, TeamIcon, UserCardItem, useToast } from "@flat-ui";
import { useState } from "react";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { useMembersUtils } from "@hooks/use-member-utils";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { getPendingUserIcon } from "@utils/user-utils";
import { UtilsTypes } from "@stellar/utils";
/** Renders a project member using the UserCardItem component */
export function ProjectMember({
  member,
  shouldHideRemove,
  isRoleEditable,
  onRemove,
  projectId,
  location
}) {
  const {
    openToast
  } = useToast();
  const {
    openProfilePage,
    openViewTeamMembers
  } = useMembersUtils();
  const companyId = useCompanyId();
  const {
    changeMemberRole
  } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "project",
    memberIdentity: member.identity,
    currentRole: member.role,
    selectedProject: {
      id: projectId
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(member.role);

  /** Is the member a team */
  const isMemberTeam = FaroCoreApiUtils.Member.isTeam(member);
  const roleDisplayName = UtilsTypes.RoleDisplayNames[member.role];

  /** Applies the role picked in the dropdown */
  async function onRoleChange(role) {
    // Prevent duplicate submissions while already loading
    if (isLoading || !isRoleEditable) {
      return;
    }
    try {
      setIsLoading(true);

      // Update the selected role immediately for a responsive UI as happy path,
      // but revert to the original role if the API call fails.
      setSelectedRole(role);

      // The user can cancel giving up their own admin role
      if (!(await changeMemberRole(role))) {
        setSelectedRole(member.role);
      }
    } catch {
      // Errors are handled by the respective slices

      // Revert to the original role if it fails to update the role
      setSelectedRole(member.role);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Opens the profile page of the user or the team page members.
   * The permission validation is done in the canOpenProfilePage variable.
   */
  function openProfileOrTeamMembersClick(member_0) {
    if (!isMemberTeam) {
      openProfilePage({
        member: member_0,
        source: "user more info menu project member list"
      });
      return;
    }
    if (member_0.id) {
      openViewTeamMembers({
        teamId: member_0.id
      });
      return;
    }
    openToast({
      title: `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} profile page cannot be opened`,
      message: `The ${TEAM_DISPLAY_NAME} ID is not available. Please reload the page to try again.`,
      variant: "error"
    });
  }

  /**
   * Returns the dropdown to change the role or remove the member, or just the role when the
   * dropdown would offer neither
   */
  function getRoleAction() {
    if (!isRoleEditable && shouldHideRemove) {
      return <MemberRoleReadOnly role={selectedRole} variant="borderless" />;
    }
    return <SelectProjectRole selectedRole={selectedRole} onChange={role_0 => void onRoleChange(role_0)} variant="borderless" isRoleEditable={isRoleEditable} onRemove={shouldHideRemove ? undefined : () => onRemove(member)} />;
  }
  return <UserCardItem key={member.identity} shouldShowProfileCard={true} actionButtonLabel={isMemberTeam ? "View Members" : "Profile"} onProfileCardClick={() => openProfileOrTeamMembersClick(member)} icon={isMemberTeam ? <TeamIcon /> : getPendingUserIcon(member)} displayOption={FaroCoreApiUtils.Member.isMemberActive(member) ? "nameOnly" : "emailOnly"} avatarSx={isMemberTeam ? {
    bgcolor: grey[700]
  } : undefined} isLoading={isLoading}
  // The role and the removal are offered by the action component instead
  shouldHideRemove userInfo={{
    name: member.name,
    identity: member.identity,
    email: isMemberTeam ? "" : member.email,
    profileImageUrl: member.thumbnailUrl,
    userRole: roleDisplayName
  }} selectedOption={selectedRole} actionComponent={getRoleAction()} />;
}